import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import { fetchSpaEntityParams } from './types';
import { sortEntityByPriority } from '@utils/helpers';
import { PaginatedSpaEntityType } from 'shared-types/Spa';
import { SPA_AVAILABILITY_TIMEOUT } from './consts';
import { Routes } from '@common/constants';

export const getSpaFacilities = async (
  propertyId: number,
  language: string,
  params: fetchSpaEntityParams
) => {
  const { data } = await request.get(`${propertyId}/facilities/${language}`, {
    params,
    timeout: SPA_AVAILABILITY_TIMEOUT,
  });
  const facilities = data.data.map((facility) => ({
    ...facility,
    linkRoute: Routes.FACILITY_DETAIL,
    linkParams: { id: facility.id },
  }));
  return { data: facilities, total: data.total } as PaginatedSpaEntityType;
};

export const getAvailableSpaFacilitiesPrefetch = async (
  queryClient: QueryClient,
  propertyId: number,
  language: string,
  arrivalDate: string | null,
  category: number | null,
  perPage = 0,
  sortByPriority = true,
  guests?: number
) => {
  await queryClient.prefetchQuery(
    getAvailableSpaFacilitiesKey(propertyId, language, {
      arrivalDate,
      category,
      perPage,
      sortByPriority,
      guests,
    }),
    () =>
      getSpaFacilities(propertyId, language, {
        arrivalDate,
        category,
        perPage,
        numberOfGuests: guests,
      }),
    { cacheTime: Infinity, staleTime: Infinity }
  );
};

export const useGetSpaFacilities = (
  propertyId: number,
  language: string,
  arrivalDate: string | null,
  category: number | null,
  perPage = 0,
  sortByPriority = true,
  guests?: number
) => {
  const { data, isFetching, isError } = useQuery({
    queryKey: getAvailableSpaFacilitiesKey(propertyId, language, {
      arrivalDate,
      category,
      perPage,
      sortByPriority,
      guests,
    }),
    queryFn: () =>
      getSpaFacilities(propertyId, language, {
        arrivalDate,
        category,
        perPage,
        numberOfGuests: guests,
      }),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
  });

  if (!data?.data) {
    return { data: [], isFetching, isError, total: 0 };
  }

  const spaFacilities = sortByPriority ? sortEntityByPriority(data?.data) : data?.data;

  return { data: spaFacilities, isFetching, isError, total: data?.total };
};

export const getAvailableSpaFacilitiesKey = (
  propertyId: number,
  language: string,
  params: {
    category: number | null;
    perPage?: number;
    arrivalDate: string | null;
    sortByPriority: boolean;
    guests?: number;
  }
) => ['spaFacilities', 'available', propertyId, ...Object.values(params), language];
